import type { TopCategoryViewModel } from 'api/types/top-category';
import type { ProductTypeViewModel } from 'api/types/product-type';
import type { SpecConstraintViewModel } from 'api/types/product-type/SpecConstraintViewModel';
import type { ConditionViewModel } from 'api/types/condition';
import type { CityViewModel } from 'api/types/city';
import type { DistrictViewModel } from 'api/types/district';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { ListingFilterState, SellerAccountTypeFilter, SpecOperator, TimePreset } from 'types/listingFilterState';
import Button from 'components/Button';
import SlidingCategoryNavigator from './SlidingCategoryNavigator';
import SpecInlineFilterPanel from './SpecInlineFilterPanel';
import {
  formatListingCreatedAtYmdToDmy,
  parseListingCreatedAtDmyToYmd,
} from 'utils/listingFilterDateInput';
import { sortCities } from 'utils/sortCities';

type FilterPanelKey = 'specs' | 'durum' | 'konum' | 'fiyat' | 'seller';

const SELLER_TYPE_OPTIONS: { label: string; value: SellerAccountTypeFilter | null }[] = [
  { label: 'Tümü', value: null },
  { label: 'Bireysel', value: 'Personal' },
  { label: 'Kurumsal', value: 'Corporate' },
];

const TIME_PRESETS: { label: string; value: TimePreset }[] = [
  { label: 'Son 24 saat', value: '24h' },
  { label: 'Son 7 gün', value: '7d' },
  { label: 'Son 30 gün', value: '30d' },
];

export interface AdFilterProps {
  topCategories: TopCategoryViewModel[];
  secondaryCategories: { id: number; name?: string | null }[];
  productTypes: ProductTypeViewModel[];
  constraints: SpecConstraintViewModel[];
  constraintsLoading?: boolean;
  constraintsError?: string | null;
  conditions: ConditionViewModel[];
  cities: CityViewModel[];
  districts: DistrictViewModel[];
  draft: ListingFilterState;
  /** Applied filters (after Apply) for summary display. */
  applied: ListingFilterState;
  setDraft: React.Dispatch<React.SetStateAction<ListingFilterState>>;
  setDraftCategory: (level: 'top' | 'secondary' | 'productType', id: number | null) => void;
  setDraftSpecValue: (key: string, value: string) => void;
  setDraftSpecMultiValue: (key: string, values: string[]) => void;
  setDraftSpecOperator: (key: string, operator: SpecOperator) => void;
  setDraftPrice: (field: 'lowerBound' | 'upperBound', value: number | null) => void;
  onApply: () => void;
  onClearFilters: () => void;
}

export default function AdFilter({
  topCategories,
  secondaryCategories,
  productTypes,
  constraints,
  constraintsLoading = false,
  constraintsError = null,
  conditions,
  cities,
  districts,
  draft,
  applied,
  setDraft,
  setDraftCategory,
  setDraftSpecValue,
  setDraftSpecMultiValue,
  setDraftSpecOperator,
  setDraftPrice,
  onApply,
  onClearFilters,
}: AdFilterProps) {
  const [openPanels, setOpenPanels] = useState<Record<FilterPanelKey, boolean>>({
    specs: true,
    durum: true,
    konum: true,
    fiyat: true,
    seller: true,
  });
  const [createdAtFromText, setCreatedAtFromText] = useState(() =>
    draft.createdAtFrom ? formatListingCreatedAtYmdToDmy(draft.createdAtFrom) : ''
  );
  const nativeDateInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setCreatedAtFromText(draft.createdAtFrom ? formatListingCreatedAtYmdToDmy(draft.createdAtFrom) : '');
  }, [draft.createdAtFrom]);

  const openNativeDatePicker = useCallback(() => {
    const el = nativeDateInputRef.current;
    if (!el) return;
    try {
      if (typeof el.showPicker === 'function') {
        void el.showPicker();
        return;
      }
    } catch {
      /* showPicker can throw if unsupported or blocked */
    }
    el.click();
  }, []);

  const flushCreatedAtFromText = useCallback(() => {
    const trimmed = createdAtFromText.trim();
    if (trimmed === '') {
      setDraft((prev) => ({ ...prev, createdAtFrom: null, timePreset: null }));
      setCreatedAtFromText('');
      return;
    }
    const ymd = parseListingCreatedAtDmyToYmd(trimmed);
    if (!ymd) {
      setCreatedAtFromText(
        draft.createdAtFrom ? formatListingCreatedAtYmdToDmy(draft.createdAtFrom) : ''
      );
      return;
    }
    setDraft((prev) => ({ ...prev, createdAtFrom: ymd, timePreset: null }));
    setCreatedAtFromText(formatListingCreatedAtYmdToDmy(ymd));
  }, [createdAtFromText, draft.createdAtFrom, setDraft]);

  const togglePanel = (key: FilterPanelKey) => {
    setOpenPanels((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const appliedProductName =
    applied.productTypeId != null
      ? productTypes.find((p) => p.id === applied.productTypeId)?.name ??
        topCategories
          .flatMap((t) => t.secondaryCategories ?? [])
          .flatMap((s) => s.productTypes ?? [])
          .find((p) => p.id === applied.productTypeId)?.name
      : null;

  return (
    <div className="card mb-4 sb-filter-panel">
      <div className="card-body">
        <h5 className="card-title mb-3 text-primary">
          <i className="fas fa-filter me-2" />
          Filtreler
        </h5>

        {/* Kategoriler — sliding */}
        <div className="category-section mb-4">
          <h6 className="fw-bold border-bottom pb-2 mb-3">Kategoriler</h6>
          <SlidingCategoryNavigator
            topCategories={topCategories}
            secondaryCategories={secondaryCategories}
            productTypes={productTypes}
            draft={draft}
            setDraftCategory={setDraftCategory}
          />
        </div>

        {/* Oluşturulma tarihi */}
        <div className="mb-4">
          <h6 className="fw-bold border-bottom pb-2 mb-3">İlan tarihi</h6>
          <div
            className="d-grid gap-2 mb-2"
            style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}
          >
            {TIME_PRESETS.map((p) => (
              <Button
                key={p.value}
                type="button"
                className={`btn btn-sm text-wrap text-center lh-sm py-2 px-1 d-flex align-items-center justify-content-center ${
                  draft.timePreset === p.value ? 'btn-primary' : 'btn-outline-primary'
                }`}
                style={{ minHeight: '3.25rem' }}
                onClick={() =>
                  setDraft((prev) => ({
                    ...prev,
                    timePreset: draft.timePreset === p.value ? null : p.value,
                    createdAtFrom: null,
                  }))
                }
              >
                {p.label}
              </Button>
            ))}
          </div>
          <div className="mb-2">
            <label htmlFor="filter-created-at-from" className="form-label small mb-1">
              Şu tarihten itibaren
            </label>
            <div className="input-group input-group-sm">
              <input
                id="filter-created-at-from"
                type="text"
                className="form-control"
                inputMode="numeric"
                autoComplete="off"
                placeholder="Tarih Seçin"
                maxLength={10}
                value={createdAtFromText}
                onChange={(e) => {
                  const v = e.target.value;
                  setCreatedAtFromText(v);
                  const trimmed = v.trim();
                  setDraft((prev) => {
                    const clearedPreset = { ...prev, timePreset: null };
                    if (trimmed === '') {
                      return { ...clearedPreset, createdAtFrom: null };
                    }
                    const ymd = parseListingCreatedAtDmyToYmd(trimmed);
                    if (ymd) {
                      return { ...clearedPreset, createdAtFrom: ymd };
                    }
                    return { ...clearedPreset, createdAtFrom: prev.createdAtFrom };
                  });
                }}
                onBlur={flushCreatedAtFromText}
              />
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={openNativeDatePicker}
                aria-label="Takvimden tarih seçin"
              >
                <i className="fas fa-calendar" aria-hidden />
              </button>
            </div>
            <input
              ref={nativeDateInputRef}
              type="date"
              className="visually-hidden"
              tabIndex={-1}
              aria-hidden
              value={draft.createdAtFrom ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setDraft((prev) => ({
                  ...prev,
                  createdAtFrom: v === '' ? null : v,
                  timePreset: null,
                }));
              }}
            />
          </div>
        </div>

        {/* Spec constraints (below category) */}
        {draft.productTypeId != null && (
          <div key={draft.productTypeId || 'no-product'} className="spec-filters mb-4">
            <h6 className="fw-bold border-bottom pb-2 mb-3">Özellikler</h6>
            <div className="accordion mb-3">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <Button
                    type="button"
                    className={`accordion-button ${openPanels.specs ? '' : 'collapsed'}`}
                    onClick={() => togglePanel('specs')}
                    aria-expanded={openPanels.specs}
                  >
                    Özellik filtreleri
                  </Button>
                </h2>
                <div className={`accordion-collapse collapse ${openPanels.specs ? 'show' : ''}`}>
                  <div className="accordion-body">
                    <SpecInlineFilterPanel
                      constraints={constraints}
                      mode="filter"
                      specValues={draft.specValues}
                      specMultiValues={draft.specMultiValues}
                      specOperators={draft.specOperators}
                      onSpecValueChange={setDraftSpecValue}
                      onSpecMultiValueChange={setDraftSpecMultiValue}
                      onSpecOperatorChange={setDraftSpecOperator}
                      loading={constraintsLoading}
                      error={constraintsError}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Non-spec filters */}
        <div className="filter-options-section">
          <h6 className="fw-bold border-bottom pb-2 mb-3">Filtreleme Seçenekleri</h6>

          <div className="accordion mb-3">
            <div className="accordion-item">
              <h2 className="accordion-header">
                <Button
                  type="button"
                  className={`accordion-button ${openPanels.seller ? '' : 'collapsed'}`}
                  onClick={() => togglePanel('seller')}
                  aria-expanded={openPanels.seller}
                >
                  Satıcı tipi
                </Button>
              </h2>
              <div className={`accordion-collapse collapse ${openPanels.seller ? 'show' : ''}`}>
                <div className="accordion-body">
                  <div
                    className="d-flex flex-wrap gap-2"
                  >
                    {SELLER_TYPE_OPTIONS.map((opt) => (
                      <Button
                        key={opt.label}
                        type="button"
                        className={`btn btn-sm flex-grow-1 ${
                          draft.sellerAccountType === opt.value ? 'btn-primary' : 'btn-outline-primary'
                        }`}
                        style={{ minWidth: '5.5rem', maxWidth: '100%' }}
                        onClick={() =>
                          setDraft((prev) => ({ ...prev, sellerAccountType: opt.value }))
                        }
                      >
                        {opt.label}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="accordion mb-3">
            <div className="accordion-item">
              <h2 className="accordion-header">
                <Button
                  type="button"
                  className={`accordion-button ${openPanels.durum ? '' : 'collapsed'}`}
                  onClick={() => togglePanel('durum')}
                  aria-expanded={openPanels.durum}
                >
                  Durum
                </Button>
              </h2>
              <div className={`accordion-collapse collapse ${openPanels.durum ? 'show' : ''}`}>
                <div className="accordion-body">
                  <select
                    className="form-select"
                    value={draft.conditionId ?? ''}
                    onChange={(e) =>
                      setDraft((prev) => {
                        return { ...prev, conditionId: e.target.value === '' ? null : Number(e.target.value) };
                      })
                    }
                  >
                    <option value="">Tümü</option>
                    {conditions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name ?? c.id}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="accordion mb-3">
            <div className="accordion-item">
              <h2 className="accordion-header">
                <Button
                  type="button"
                  className={`accordion-button ${openPanels.konum ? '' : 'collapsed'}`}
                  onClick={() => togglePanel('konum')}
                  aria-expanded={openPanels.konum}
                >
                  Konum
                </Button>
              </h2>
              <div className={`accordion-collapse collapse ${openPanels.konum ? 'show' : ''}`}>
                <div className="accordion-body">
                  <select
                    className="form-select mb-2"
                    value={draft.cityId ?? ''}
                    onChange={(e) =>
                      setDraft((prev) => ({
                        ...prev,
                        cityId: e.target.value === '' ? null : Number(e.target.value),
                        districtId: null,
                      }))
                    }
                  >
                    <option value="">Tüm Şehirler</option>
                    {sortCities(cities).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name ?? c.id}
                      </option>
                    ))}
                  </select>
                  {draft.cityId != null && (
                    <select
                      className="form-select"
                      value={draft.districtId ?? ''}
                      onChange={(e) =>
                        setDraft((prev) => ({
                          ...prev,
                          districtId: e.target.value === '' ? null : Number(e.target.value),
                        }))
                      }
                    >
                      <option value="">Tüm İlçeler</option>
                      {districts.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name ?? d.id}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="accordion mb-3">
            <div className="accordion-item">
              <h2 className="accordion-header">
                <Button
                  type="button"
                  className={`accordion-button ${openPanels.fiyat ? '' : 'collapsed'}`}
                  onClick={() => togglePanel('fiyat')}
                  aria-expanded={openPanels.fiyat}
                >
                  Fiyat Aralığı
                </Button>
              </h2>
              <div className={`accordion-collapse collapse ${openPanels.fiyat ? 'show' : ''}`}>
                <div className="accordion-body">
                  <div className="row g-2">
                    <div className="col-6">
                      <input
                        type="number"
                        className="form-control sb-no-number-spinner"
                        placeholder="En Az"
                        value={draft.price.lowerBound ?? ''}
                        onChange={(e) => setDraftPrice('lowerBound', e.target.value === '' ? null : Number(e.target.value))}
                      />
                    </div>
                    <div className="col-6">
                      <input
                        type="number"
                        className="form-control sb-no-number-spinner"
                        placeholder="En Çok"
                        value={draft.price.upperBound ?? ''}
                        onChange={(e) => setDraftPrice('upperBound', e.target.value === '' ? null : Number(e.target.value))}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {applied.productTypeId != null && (
          <div className="alert alert-light border small py-2 mb-0" role="status">
            <strong>Uygulanan ürün tipi:</strong> {appliedProductName ?? `#${applied.productTypeId}`}
          </div>
        )}
      </div>

      <div className="card-footer border-top" style={{ backgroundColor: 'var(--sb-bg)' }}>
        <Button
          type="button"
          className="btn btn-primary w-100"
          onClick={() => {
            flushCreatedAtFromText();
            onApply();
          }}
        >
          Filtreleri Uygula
        </Button>
        <Button type="button" className="btn btn-outline-secondary w-100 mt-2" onClick={onClearFilters}>
          Filtreleri Temizle
        </Button>
      </div>
    </div>
  );
}
